import type {
  CheckListProps,
  CustomBlockProps,
  RelatedFieldObject,
  ReportSignature
} from 'common/models';
import { getFullName } from 'helpers/contactPersonHelpers';
import type { OrderMaterial, TravelCost, Work } from 'pages/orders/models';
import type { ServiceObjectInCustomer } from 'pages/serviceObjects/models';
import React from 'react';

import CompanyBlock from './CompanyBlock';
import CustomDataCheckList from './CustomDataCheckList';
import CustomerBlockWrapper from './CustomerBlockWrapper';
import CustomerSignatureBlock from './CustomerSignatureBlock';
import MaterialBlock from './MaterialBlock';
import OrderCodeBlock from './OrderCodeBlock';
import ServiceObjectsInfoBlock from './ServiceObject/Info/ServiceObjectsInfoBlock';
import TechnicianSignatureBlock from './TechnicianSignatureBlock';
import TitleBlock from './TitleBlock';
import TotalCostBlock from './TotalCostBlock';
import TravelBlock from './TravelBlock';
import WorkBlock from './WorkBlock';

interface Props {
  blockProps: CustomBlockProps;
  object: RelatedFieldObject;
  blockData?: any;
  signature?: ReportSignature;
  serviceObjectId?: string;
  reportId: string;
  customFormId: string;
  perServiceObject?: boolean;
  reportTitle: string;
  checkListProps?: CheckListProps;
  totalCost: number;
  serviceObjects: ServiceObjectInCustomer[];
  isPublic?: boolean;
  customFormCodeNumber: number;
}

const CustomDataBlockInner: React.FC<Props> = (props): React.ReactElement => {
  const {
    blockProps,
    object,
    blockData,
    signature,
    serviceObjectId,
    reportId,
    customFormId,
    perServiceObject,
    reportTitle,
    checkListProps,
    totalCost,
    serviceObjects,
    isPublic,
    customFormCodeNumber
  } = props;

  const { currencyId } = object.company!;
  const pricePerKm = object.company!.pricePerKilometer;

  const { hasFlatRateTravelFee } = object.order!.address;
  const companyTravelFee = object.company?.travelFee;
  const addressTravelFee = object.order!.address.travelFee;
  const travelFee = addressTravelFee || companyTravelFee || 0;
  const contactPerson = object.order!.contactPerson;

  if (blockProps.blockType === 'Title') {
    return <TitleBlock customTitle={blockProps.customTitle} title={reportTitle} />;
  }

  if (blockProps.blockType === 'Customer') {
    return <CustomerBlockWrapper blockProps={blockProps} object={object} />;
  }

  if (blockProps.blockType === 'Company') {
    return (
      <CompanyBlock
        company={
          blockProps.customContent
            ? { ...blockProps.customContent, logoUrl: object.company!.logoUrl }
            : object.company!
        }
        customTitle={blockProps.customTitle}
        withTitle={blockProps.withTitle}
        hideLogo={blockProps.hideLogo}
      />
    );
  }

  if (blockProps.blockType === 'Footer') {
    return <div className="FooterBlock">{blockProps.footerText}</div>;
  }

  if (blockProps.blockType === 'TravelCosts' && blockData) {
    return (
      <TravelBlock
        travel={blockData as TravelCost[]}
        title={blockProps.title}
        currencyId={currencyId}
        hasFlatRateTravelFee={hasFlatRateTravelFee}
        pricePerKm={pricePerKm}
        travelFee={travelFee}
        showPrice={!blockProps.hidePrice}
      />
    );
  }

  if (blockProps.blockType === 'WorkTable' && blockData) {
    return (
      <WorkBlock
        work={blockData as Work[]}
        title={blockProps.title}
        currencyId={currencyId}
        showPrice={!blockProps.hidePrice}
        assignee={blockProps.assignee}
        description={blockProps.description}
        assignations={object.intervention?.assignations}
      />
    );
  }

  if (blockProps.blockType === 'Material' && blockData) {
    return (
      <MaterialBlock
        material={blockData as OrderMaterial[]}
        title={blockProps.title}
        currencyId={currencyId}
        showPrice={!blockProps.hidePrice}
        groupByKey={blockProps.groupBy}
        visibleColumns={blockProps.visibleColumns}
      />
    );
  }

  if (blockProps.blockType === 'TotalCost' && blockData) {
    return <TotalCostBlock currencyId={currencyId} totalCost={totalCost} />;
  }

  if (blockProps.blockType === 'OrderCode') {
    return (
      <OrderCodeBlock
        showFormNumber={blockProps.showFormNumber}
        key={blockProps.blockType}
        order={object.order!}
        customFormCodeNumber={customFormCodeNumber}
      />
    );
  }

  if (blockProps.blockType === 'CustomerSignature') {
    return (
      <CustomerSignatureBlock
        key={blockProps.blockType}
        withDate={blockProps.withDate}
        withName={blockProps.withName}
        withPosition={blockProps.withPosition}
        signature={signature}
        canSign={object.intervention?.state === 'WaitingToSignature'}
        serviceObjectId={serviceObjectId}
        reportId={reportId}
        customFormId={customFormId}
        orderId={object.order?.id || ''}
        customDate={blockProps.customDate}
        customName={blockProps.customName}
        customPosition={blockProps.customPosition}
        contactPerson={contactPerson}
        company={object.company}
        perServiceObject={perServiceObject}
        interventionId={object.intervention?.id || ''}
        primaryAssigneeId={object.intervention?.primaryAssigneeId || ''}
        isPublic={isPublic}
        customTitle={blockProps.customTitle}
      />
    );
  }

  if (blockProps.blockType === 'TechnicianSignature') {
    return (
      <TechnicianSignatureBlock
        realStart={object.intervention?.startedAt || ''}
        technicianSignatureUrl={object.primaryAssignee?.signatureUrl}
        customDate={blockProps.customDate}
        withName={blockProps.withName}
        withDate={blockProps.withDate}
        technicianName={object.primaryAssignee && getFullName(object.primaryAssignee)}
        companyStampUrl={object.company?.stampUrl}
        customName={blockProps.customName}
        customTitle={blockProps.customTitle}
        hideStamp={blockProps.hideStamp}
      />
    );
  }

  if (blockProps.blockType === 'CheckList' && checkListProps) {
    return (
      <CustomDataCheckList
        checkListProps={checkListProps}
        data={blockData}
        title={blockProps.title}
      />
    );
  }

  if (blockProps.blockType === 'ServiceObjectsInfo') {
    return (
      <ServiceObjectsInfoBlock
        title={blockProps.title}
        mode={blockProps.mode}
        serviceObjects={serviceObjects}
        serviceObjectManufacturers={object.codebooks?.serviceObjectManufacturers || []}
      />
    );
  }

  return <div />;
};

const CustomDataBlock: React.FC<Props> = (props): React.ReactElement => {
  return (
    <div className={`CustomDataBlock ${props.blockProps.enablePageBreak ? 'enablePageBreak' : ''}`}>
      <CustomDataBlockInner {...props} />
    </div>
  );
};

export default CustomDataBlock;
